// @ts-strict-ignore
import {Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from "@angular/core";
import {EventEmitterService} from "@event-emitter";
import {SimCameraService, SimConfigurationService, SimMaterialDialogService, SimUtilityService} from "@services";
import {TranslateService} from "@ngx-translate/core";
import {CameraProxyStatus, ConnectivityState} from "@models";

@Component({
	selector: "sim-rtspwebrtc-video",
	templateUrl: "./rtspwebrtc-video.component.html",
	styleUrls: ["./rtspwebrtc-video.component.scss"],
	providers: [EventEmitterService],
	standalone: false,
})
export class RtspwebrtcVideoComponent implements OnInit, OnChanges, OnDestroy {
	ConnectivityState = ConnectivityState;
	CameraProxyStatus = CameraProxyStatus;

	@ViewChild("videoElement", {static: true})
	videoElement: ElementRef<HTMLVideoElement>;

	@Input() rtspwebrtcStreamId = "";
	@Input() rtspwebrtcProxyUrl = "";
	@Input() rtspwebrtcProxyStatus: CameraProxyStatus;
	@Input() rtspwebrtcVideoStyle = "";
	@Input() rtspwebrtcVideoClass = "";
	@Input() streamName = "";

	@Input() sourceVideoOptions: {
		cameraId: string;
		ipAddress: string;
		url: string;
		anonymousAccessAllowed: boolean;
		status: ConnectivityState;
	} = null;

	public settings = SimConfigurationService.settings;

	public videoId = "";
	public deviceStatus: ConnectivityState;
	public currentState: "Ready" | "Offline" | "Loading";
	private isPlayingStream = false;

	private sendChannel: RTCDataChannel = null;
	private stream: MediaStream = null;
	private suuid: string = null;
	private streamId: string = null;
	private webrtcPc: RTCPeerConnection = null;

	private eventHandler: any = null;
	private keepAliveInterval: ReturnType<typeof setInterval> = null;
	private checkInterval: ReturnType<typeof setInterval> = null;

	constructor(
		private _simUtilityService: SimUtilityService,
		private _simCameraService: SimCameraService,
		private _simDialogService: SimMaterialDialogService,
		private translate: TranslateService,
		private _eventEmitter: EventEmitterService,
	) {
		this.videoId = SimUtilityService.createGuid();
		this.eventHandler = this.handleNegotiationNeededEvent.bind(this);
	}

	public ngOnInit(): void {
		this._eventEmitter.onSystemHubReconnected.subscribe(async () => {
			await this.checkCameraStatus();
		});

		this._eventEmitter.onCameraStateChanged.subscribe(async (args) => {
			if (this.rtspwebrtcStreamId === args.cameraState.id) {
				this.updateCameraStatus(args.cameraState);
			}
		});

		this.checkInterval = setInterval(async () => {
			this.checkConnectivity();
		}, 1000);
	}

	public canPlay(event: Event) {
		this.currentState = "Ready";
		this.videoElement.nativeElement.play();
	}

	public playing(event: Event) {
		this.isPlayingStream = true;
	}

	public async ngOnChanges(changesObj: SimpleChanges) {
		const streamId = changesObj["rtspwebrtcStreamId"];
		const streamName = changesObj["streamName"];

		if (streamId || streamName) {
			this.cleanup(true);
			await this.checkCameraStatus();
		}
	}

	public ngOnDestroy() {
		clearInterval(this.checkInterval);
		this.cleanup(true);
	}

	private isReadyToStart(): boolean {
		return (
			!!this.rtspwebrtcStreamId &&
			this.deviceStatus === ConnectivityState.Online &&
			(!this.settings.rtspToWebRtcCentralInstanceEnabled || this.rtspwebrtcProxyStatus === CameraProxyStatus.Available)
		);
	}

	private checkConnectivity() {
		if (
			this.isPlayingStream &&
			(this.deviceStatus === ConnectivityState.Offline || this.rtspwebrtcProxyStatus !== CameraProxyStatus.Available)
		) {
			this.cleanup(true);
		} else {
			// Check if we should attempt opening the stream
			if (!this.isPlayingStream && this.isReadyToStart() && this.currentState !== "Loading") {
				this.currentState = "Loading";
				setTimeout(() => {
					if (this.currentState == "Loading") this.currentState = "Offline";
				}, 5000);

				// On a central instance of the restreamer, each video source is accessed through its identifier (camera id).
				// On decentral restreamers, each device has the same url for the liveview
				this.suuid = this.rtspwebrtcStreamId;
				if (this.settings.rtspToWebRtcCentralInstanceEnabled) {
					this.streamId = this.rtspwebrtcStreamId;
				} else {
					this.streamId = "liveview";
				}

				this.startStream();
			}
		}
	}

	private async checkCameraStatus() {
		let result = await this._simCameraService.getCameraById(this.rtspwebrtcStreamId);
		this.updateCameraStatus(result);
	}

	private updateCameraStatus(cameraState: {proxyStatus: CameraProxyStatus; status: ConnectivityState}) {
		if (this.deviceStatus !== cameraState.status || this.rtspwebrtcProxyStatus !== cameraState.proxyStatus) {
			this.rtspwebrtcProxyStatus = cameraState.proxyStatus;
			this.deviceStatus = cameraState.status;

			this.checkConnectivity();
		}
	}

	private startStream() {
		const config = {
			iceServers: [
				{
					urls: ["stun:stun.l.google.com:19302"],
				},
			],
		};
		// We can enter stun/turn servers if necessary, possibly in the future due to chrome prohibiting anonymous WebRTC
		// urls: ["stun:stun.l.google.com:19302"]

		if (this.webrtcPc) {
			this.cleanup(true);
		}

		this.stream = new MediaStream();
		this.webrtcPc = new RTCPeerConnection(config);
		this.webrtcPc.onnegotiationneeded = this.eventHandler;
		this.webrtcPc.onconnectionstatechange = () => {
			const states = ["closed", "disconnected", "failed"];
			if (states.includes(this.webrtcPc?.connectionState)) {
				this.cleanup(true);
			}
		};
		this.webrtcPc.addTransceiver("video", {
			direction: "sendrecv",
		});

		this.tryConnection();

		this.webrtcPc.ontrack = (event) => {
			console.log("Adding track" + event.track);
			this.stream.addTrack(event.track);
			this.videoElement.nativeElement.srcObject = this.stream;
		};

		//this.playWebRtc();
	}

	public async handleNegotiationNeededEvent() {
		const offer = await this.webrtcPc.createOffer();

		await this.webrtcPc.setLocalDescription(offer);
		this.playWebRtc();
	}

	public playWebRtc() {
		let channelIdentifier = "0";
		if (this.streamName.length > 0) {
			channelIdentifier = this.streamName;
		}

		const streamUrl = `${this.rtspwebrtcProxyUrl}/stream/${this.streamId}/channel/${channelIdentifier}/webrtc?uuid=${this.streamId}&channel=${channelIdentifier}`;
		$.post(
			streamUrl,
			{
				data: btoa(this.webrtcPc.localDescription.sdp),
			},
			(data) => {
				if (!this.isPlayingStream && !!this.webrtcPc) {
					this.webrtcPc.setRemoteDescription(
						new RTCSessionDescription({
							type: "answer",
							sdp: atob(data),
						}),
					);
				} else {
					console.warn(
						"Tried to add transceiver for codec but isPlayingStream was " +
							this.isPlayingStream +
							" and connection was " +
							this.webrtcPc,
					);
					this.cleanup(true);
				}
			},
		);
	}

	private tryConnection() {
		this.cleanup();

		this.sendChannel = this.webrtcPc.createDataChannel(this.suuid);
		this.sendChannel.onclose = () => {
			this.isPlayingStream = false;
			this.currentState = "Offline";
			if (this.keepAliveInterval) {
				clearInterval(this.keepAliveInterval);
			}
		};
		this.sendChannel.onopen = () => {
			this.sendChannel.send("ping");
			this.keepAliveInterval = setInterval(() => {
				this.sendChannel.send("ping");
			}, 1000);
		};
	}

	private cleanup(includeConnection: Boolean = false) {
		if (this.webrtcPc && includeConnection) {
			this.webrtcPc.close();
			this.webrtcPc.onnegotiationneeded = null;
			this.webrtcPc.onconnectionstatechange = null;
			this.webrtcPc.ontrack = null;
			this.webrtcPc = null;
		}

		if (this.keepAliveInterval) {
			clearInterval(this.keepAliveInterval);
		}

		this.isPlayingStream = false;

		if (this.sendChannel) {
			this.sendChannel.onclose = null;
			this.sendChannel.close();
		}
		this.sendChannel = null;
	}

	public fireOnMouseEnter() {}

	public fireOnMouseLeave() {}
}
